import { z } from 'zod';
import { MAX_CUSTOM_PAGE_TEXT, richTextDocumentSchema, richTextToPlainText, } from '../../rich-text.js';
/**
 * Custom data: custom pages an Account User writes for the assistant, with
 * information that isn't on the website (`product/domain-glossary.md`, Custom
 * page; `features/website-knowledge/technical.md`, API contracts 8-12). A
 * custom page lives in Groundly only and has no public link. It is knowledge
 * as soon as it is saved. Every route needs the knowledge permission.
 */
export const CUSTOM_PAGE_MESSAGES = {
    titleEmpty: 'Enter a title so you can find this page in the list.',
    contentEmpty: 'Add the information the assistant may use before saving the custom page.',
    contentTooLong: `Keep the information under ${MAX_CUSTOM_PAGE_TEXT.toLocaleString('en')} characters.`,
};
const websiteParams = z.object({ websiteId: z.uuid() });
const customPageParams = websiteParams.extend({ customPageId: z.uuid() });
export const customPageBodySchema = z.object({
    title: z.string().trim().min(1, CUSTOM_PAGE_MESSAGES.titleEmpty).max(200),
    content: richTextDocumentSchema
        .refine((doc) => richTextToPlainText(doc).trim() !== '', {
        message: CUSTOM_PAGE_MESSAGES.contentEmpty,
    })
        .refine((doc) => richTextToPlainText(doc).length <= MAX_CUSTOM_PAGE_TEXT, {
        message: CUSTOM_PAGE_MESSAGES.contentTooLong,
    }),
});
export const customPageSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    /** The formatted text, as the editor's document tree. */
    content: z.unknown(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const customPageListItemSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    /** The start of the text, without formatting. */
    excerpt: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const listCustomPagesResponseSchema = z.object({
    items: z.array(customPageListItemSchema),
    /**
     * The custom pages the website's plan allows at once; null when it has no
     * plan this cycle. Adding one past it is RULE_BLOCKED PLAN_CUSTOM_PAGE_LIMIT.
     */
    limit: z.number().int().min(0).nullable(),
});
/** Oldest first. No pagination: a website has few custom pages. */
export const listCustomPages = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/custom-pages',
    params: websiteParams,
    query: z.object({}),
    body: z.object({}),
    response: listCustomPagesResponseSchema,
};
export const createCustomPage = {
    method: 'POST',
    path: '/api/dashboard/websites/:websiteId/custom-pages',
    params: websiteParams,
    query: z.object({}),
    body: customPageBodySchema,
    response: customPageSchema,
};
export const getCustomPage = {
    method: 'GET',
    path: '/api/dashboard/websites/:websiteId/custom-pages/:customPageId',
    params: customPageParams,
    query: z.object({}),
    body: z.object({}),
    response: customPageSchema,
};
export const updateCustomPage = {
    method: 'PATCH',
    path: '/api/dashboard/websites/:websiteId/custom-pages/:customPageId',
    params: customPageParams,
    query: z.object({}),
    body: customPageBodySchema,
    response: customPageSchema,
};
export const deleteCustomPage = {
    method: 'DELETE',
    path: '/api/dashboard/websites/:websiteId/custom-pages/:customPageId',
    params: customPageParams,
    query: z.object({}),
    body: z.object({}),
    response: z.void(),
};
//# sourceMappingURL=custom-pages.js.map